import {
  AcatDirection,
  type InvestmentActivityAcatCash,
  type InvestmentActivityAcatStock,
  type InvestmentTransferActivityDetailV2,
  InvestmentTransferType,
  useInvestmentAccountActivity,
  useWealthOverview,
} from '@egw/api'
import { Button, ButtonVariant, Field, type FieldProps, PageHeader } from '@egw/components'
import { DEFAULT_CURRENCY_CODE, WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { buildBasicCurrency, Currency, dateFormatExtended, getCurrencySymbol } from '@egw/utils'
import { InformationCircleIcon } from '@heroicons/react/24/solid'
import cn from 'classnames'
import { type FC, useCallback, useMemo } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate, useParams } from 'react-router'
import { TradeField } from '../../../components/trade-field'
import { TransactionSubway } from '../../../components/transaction-subway/transaction-subway'

interface TransactionAmountDisplayProps {
  acatDisplayAmount: { type: 'cash' | 'security' | 'multiple'; value: string } | null
  displayAmountValue: number
  currencyCode?: string
}

const TransactionAmountDisplay: FC<TransactionAmountDisplayProps> = ({
  acatDisplayAmount,
  displayAmountValue,
  currencyCode,
}) => {
  // Display ACAT security or multiple assets format
  if (acatDisplayAmount) {
    return (
      <div id="transaction_amount" className="typo-primary-3xl-semibold text-text-header">
        {acatDisplayAmount.value}
      </div>
    )
  }

  // Display standard amount format for non-ACAT transactions or ACAT cash transfers
  return (
    <>
      <div id="transaction_amount" className="flex items-baseline justify-start">
        <div className="typo-primary-4xl-semibold text-text-header">
          {Currency.onlyInteger.plusMinus(displayAmountValue)}
        </div>
        <div
          className={cn('typo-primary-2xl-light', {
            'text-text-header': displayAmountValue > 0,
            'text-text-meta': displayAmountValue <= 0,
          })}
        >
          {Currency.onlyCents.omitSign(displayAmountValue)}
        </div>
      </div>
      <div
        className={cn('font-bold', {
          'text-text-header': displayAmountValue > 0,
          'text-text-meta': displayAmountValue <= 0,
        })}
      >
        {currencyCode ?? DEFAULT_CURRENCY_CODE}
      </div>
    </>
  )
}

export const TransactionInvestmentsDetails: FC = () => {
  const { t } = useTranslation('features-transactions')
  const navigate = useNavigate()

  const { alias = '', transactionId = '' } = useParams<{ alias: string; transactionId: string }>()

  const { data, isLoading: productsLoading, isError: productsError } = useWealthOverview()

  const investmentAccount = data?.personalPortfolio?.accounts.find((account) => account.alias === parseInt(alias))

  const accountName = investmentAccount?.accountName ?? ''

  const {
    data: investmentAccountActivity,
    isLoading,
    isError,
  } = useInvestmentAccountActivity({
    alias: parseInt(alias),
  })

  const transaction = investmentAccountActivity?.activity.find((item) => item.activityId.toString() === transactionId)

  const showSubway = ((): boolean => {
    if (transaction && transaction.__typename === 'InvestmentTransferActivityDetailV2') {
      return true
    }
    return false
  })()

  const label = ((): string => {
    if (!transaction) return ''

    if (transaction.__typename === 'InvestmentTransferActivityDetailV2')
      return t(`recent_activity.labels.${transaction.transferType}`)

    if (transaction.__typename === 'InvestmentDividendActivityDetailV2') return t('recent_activity.labels.dividends')

    if (transaction.__typename === 'InvestmentInterestActivityDetail') return t('recent_activity.labels.interest')

    if (transaction.__typename === 'InvestmentAcatGroupActivityDetailV2') return t('recent_activity.labels.acat')

    // Else InvestmentFeeActivityDetail
    return t('recent_activity.labels.fee')
  })()

  const acatDisplayAmount = useMemo((): { type: 'cash' | 'security' | 'multiple'; value: string } | null => {
    if (!transaction) return null

    // Handle ACAT transfers with special formatting
    if (transaction.__typename === 'InvestmentAcatGroupActivityDetailV2') {
      const { transfers } = transaction

      // If no transfers, return null
      if (!transfers.length) return null

      // BR-03: Cash-Only Transfers - return null
      if (transfers.length === 1 && transfers[0].__typename === 'InvestmentActivityAcatCash') return null

      // BR-04: Single Security Transfers
      if (transfers.length === 1 && transfers[0].__typename === 'InvestmentActivityAcatStock') {
        const stockTransfer = transfers[0]
        const prefix = stockTransfer.direction === AcatDirection.INCOMING ? '+' : '-'
        return {
          type: 'security',
          value: `${stockTransfer.symbol}, ${prefix} ${stockTransfer.quantity.toString()}`,
        }
      }

      // BR-05: Multiple Asset Transfers
      if (transfers.length > 1) {
        // Determine direction from first transfer (all should have same direction in a group)
        const prefix = transfers[0].direction === AcatDirection.INCOMING ? '+' : '-'
        return {
          type: 'multiple',
          value: t('acat_transfer.multiple_assets', { prefix }),
        }
      }
    }

    return null
  }, [transaction, t])

  const displayAmountValue = useMemo((): number => {
    // For ACAT cash transfers, get amount from the transfer
    if (transaction?.__typename === 'InvestmentAcatGroupActivityDetailV2') {
      const { transfers } = transaction
      if (transfers.length === 1 && transfers[0].__typename === 'InvestmentActivityAcatCash') {
        return transfers[0].amount?.amount ?? 0
      }
    }

    // For all other transactions, use transaction.amount

    return transaction?.amount?.amount ?? 0
  }, [transaction])

  const sortedAcatTransfers = useMemo(() => {
    if (transaction?.__typename !== 'InvestmentAcatGroupActivityDetailV2') return []

    return [...transaction.transfers].sort((a, b) => {
      // Cash transfers first
      if (a.__typename === 'InvestmentActivityAcatCash' && b.__typename === 'InvestmentActivityAcatStock') {
        return -1
      }
      // Stock transfers after cash
      if (a.__typename === 'InvestmentActivityAcatStock' && b.__typename === 'InvestmentActivityAcatCash') {
        return 1
      }
      // Both are stock transfers - sort by quantity (most shares to least shares)
      if (a.__typename === 'InvestmentActivityAcatStock' && b.__typename === 'InvestmentActivityAcatStock') {
        return b.quantity - a.quantity
      }
      // Both are cash or default case
      return 0
    })
  }, [transaction])

  // Notes for Transactions that are pending withdrawal i.e. PROCESSING_TRADES or PROCESSING_FUNDS
  const pendingWithDrawalNotes = ((): { label: string; value: string }[] => {
    if (
      transaction &&
      transaction.__typename === 'InvestmentTransferActivityDetailV2' &&
      transaction.transferType === InvestmentTransferType.WITHDRAWAL
    ) {
      return [
        {
          label: t('activity.transaction_details.requested'),
          value: buildBasicCurrency({ ...transaction.initiatedAmount }),
        },
      ]
    }
    // if not in the status will simply not display anything
    return []
  })()

  const transactionNotes = ((): FieldProps[] => {
    if (!transaction) return []
    if (transaction.__typename === 'InvestmentTransferActivityDetailV2') {
      return [
        {
          label: t('activity.transaction_details.from'),
          value: transaction.transferType === InvestmentTransferType.DEPOSIT ? transaction.counterparty : accountName,
        },
        {
          label: t('activity.transaction_details.to'),
          value: transaction.transferType === InvestmentTransferType.DEPOSIT ? accountName : transaction.counterparty,
        },
        ...pendingWithDrawalNotes,
        {
          label: t('activity.transaction_details.initiated'),
          value: dateFormatExtended(transaction.initiatedDate),
        },
      ]
    }

    if (transaction.__typename === 'InvestmentDividendActivityDetailV2') {
      return [
        {
          label: t('activity.transaction_details.to'),
          value: accountName,
        },
        {
          label: t('activity.transaction_details.ticker'),
          value: transaction.symbol,
        },
        {
          label: t('activity.transaction_details.name'),
          value: transaction.instrumentName,
        },
        {
          label: t('activity.transaction_details.dividend_payment_date'),
          value: dateFormatExtended(transaction.date),
        },
        {
          label: t('activity.transaction_details.amount_per_share'),
          value: buildBasicCurrency({
            ...transaction.amountPerShare,
          }),
        },
      ]
    }

    if (transaction.__typename === 'InvestmentFeeActivityDetail') {
      return [
        {
          label: t('activity.transaction_details.account_charged'),
          value: accountName,
        },
        {
          label: t('activity.transaction_details.initiated'),
          value: transaction.initiatedDate
            ? dateFormatExtended(transaction.initiatedDate)
            : t('activity.transaction_details.empty_state'),
        },
      ]
    }

    if (transaction.__typename === 'InvestmentAcatGroupActivityDetailV2') {
      return [
        ...(transaction.sourceAccount
          ? [
              {
                label: t('activity.transaction_details.from'),
                value: transaction.sourceAccount,
              },
            ]
          : []),
        ...(transaction.destinationAccount
          ? [
              {
                label: t('activity.transaction_details.to'),
                value: transaction.destinationAccount,
              },
            ]
          : []),
        {
          label: t('activity.transaction_details.initiated'),
          value: dateFormatExtended(transaction.date),
        },
      ]
    }

    // else InvestmentInterestActivityDetail
    return [
      {
        label: t('activity.transaction_details.to'),
        value: accountName,
      },
      {
        label: t('activity.transaction_details.interest_payment_date'),
        value: dateFormatExtended(transaction.date),
      },
    ]
  })()

  const pageTitle = transaction ? transaction.displayName : t('activity.title')

  const getTransferLabel = useCallback(
    (transfer: InvestmentActivityAcatStock | InvestmentActivityAcatCash) => {
      return transfer.__typename === 'InvestmentActivityAcatCash' ? t('transfers.cash_transfer_label') : transfer.symbol
    },
    [t],
  )

  const getTransferValue = useCallback(
    (transfer: InvestmentActivityAcatStock | InvestmentActivityAcatCash): JSX.Element | null => {
      if (transfer.__typename === 'InvestmentActivityAcatCash') {
        return (
          <>
            <span>{getCurrencySymbol(transfer.amount?.currencyCode ?? DEFAULT_CURRENCY_CODE)}</span>
            <span>{Currency.integerCents.onlyMinus(Number(transfer.amount?.amount ?? 0))}</span>
          </>
        )
      }
      return <span>{t('acat_transfer.acat_stock_transfer_label', { quantity: transfer.quantity })}</span>
    },
    [t],
  )

  return (
    <>
      {showSubway ? (
        <div className="block md:hidden">
          {transaction ? (
            <TransactionSubway
              checkingProductName={data?.highYieldChecking?.productName ?? ''}
              investmentTransferType={(transaction as InvestmentTransferActivityDetailV2).transferType}
              transferStatus={(transaction as InvestmentTransferActivityDetailV2).transferStatus}
            />
          ) : null}
        </div>
      ) : null}
      <PageHeader
        title={pageTitle}
        subtitle={t('activity.title')}
        backLinkUrl={WEALTH_ROUTES.INVESTMENTS_ACCOUNT_ACTIVITY_DETAILS.backPath?.replace(':alias', alias.toString())}
      />
      <div className="egw-container pt-0">
        {isLoading || productsLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {transaction ? (
          <div className="flex flex-col gap-4">
            {/* Transaction Date */}
            <div id="transaction_date" className="typo-primary-xl-semibold text-text-header">
              {dateFormatExtended(transaction.date)}
            </div>

            {/* Label */}
            <div>
              <span
                data-testId="transaction_method"
                id="transaction_method"
                className="px-xs py-3xs text-text-meta rounded-full border border-gray-500"
              >
                {label}
              </span>
            </div>

            <div className="inline-flex items-baseline justify-start gap-2xs">
              <TransactionAmountDisplay
                acatDisplayAmount={acatDisplayAmount}
                displayAmountValue={displayAmountValue}
                currencyCode={transaction.amount?.currencyCode ?? DEFAULT_CURRENCY_CODE}
              />
            </div>

            {showSubway ? (
              <div className="hidden md:block">
                <TransactionSubway
                  checkingProductName={data?.highYieldChecking?.productName ?? ''}
                  investmentTransferType={(transaction as InvestmentTransferActivityDetailV2).transferType}
                  transferStatus={(transaction as InvestmentTransferActivityDetailV2).transferStatus}
                />
              </div>
            ) : null}

            {/* Transaction Notes */}
            <div className="flex flex-col mt-lg gap-sm">
              {transactionNotes.map((note) => {
                return (
                  <div key={crypto.randomUUID()} className="border-b pb-sm last-of-type:border-none">
                    <Field key={crypto.randomUUID()} {...note} />
                  </div>
                )
              })}
            </div>

            {/* Trades */}
            {transaction.__typename !== 'InvestmentAcatGroupActivityDetailV2' && transaction.trades?.length ? (
              <div className="mt-lg mb-lg">
                <Button
                  className="sm:max-w-full"
                  variant={ButtonVariant.Secondary}
                  onClick={() => {
                    void navigate(
                      WEALTH_ROUTES.INVESTMENTS_ACCOUNT_TRADES.path
                        .replace(':alias', alias.toString())
                        .replace(':transactionId', transactionId.toString()),
                    )
                  }}
                >
                  {t('activity.view_trades')}
                </Button>
              </div>
            ) : null}

            {/** Transfers (ACAT) */}
            {sortedAcatTransfers.length ? (
              <div className="mt-xl mb-lg px-2xs">
                {sortedAcatTransfers.map((transfer) => {
                  return (
                    <TradeField
                      key={crypto.randomUUID()}
                      label={getTransferLabel(transfer)}
                      value={getTransferValue(transfer)}
                      subtitle={
                        transfer.__typename === 'InvestmentActivityAcatStock' && transfer.instrumentName
                          ? transfer.instrumentName
                          : undefined
                      }
                    />
                  )
                })}
              </div>
            ) : null}
          </div>
        ) : null}

        {isError || productsError ? (
          <>
            <InformationCircleIcon className="w-2xl h-2xl fill-red-500 mb-sm" />
            <p className="text-text-error typo-primary-2xl-light mb-xl">{t('common.generic_error')}</p>
            <p className="text-text-body typo-primary-xl">{t('activity.error')}</p>
          </>
        ) : null}
      </div>
    </>
  )
}
